import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import BreadCrumb from "@surgia/ui/components/breadCrumb";
import Button from "@surgia/ui/components/button";
import { steps } from "./constant";
import { useQuery } from "@tanstack/react-query";
import { tenantQueries as adminTenantQueries } from "@surgia/api/src/queryStore/admin/tenant";
import { useParams } from "react-router-dom";
import { ADMIN_PROTECTED_ROUTES } from "../../routes";
interface ProductAssignmentLayoutProps {
  children: React.ReactNode;
  currentStep: number;
  selectedProductsCount: number;
  onPreviousStep: () => void;
  onNextStep: () => Promise<void>;
  onFinish: () => void;
  onSaveAsDraft: () => Promise<void>;
  isAssigning?: boolean;
}

const ProductAssignmentLayout: React.FC<ProductAssignmentLayoutProps> = ({
  children,
  currentStep,
  onSaveAsDraft,
  isAssigning = false,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === "ar";
  const { id: tenantId } = useParams();
  const { data: tenantResponse } = useQuery(
    adminTenantQueries.queries.tenants({
      status: "ACTIVE",
      id: +tenantId!,
    })
  );
  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      className="px-6 py-[10px] bg-white h-[100%] w-full overflow-x-auto"
    >
      {/* Top header section */}
      <div className="pl-2 pr-6 py-[10px]">
        <BreadCrumb
          items={[
            {
              label: t("admin.productAssignment.breadcrumb.tenants"),
              show: true,
              onClick: () => navigate(ADMIN_PROTECTED_ROUTES.TENANTS),
            },
            {
              label: t("admin.productAssignment.breadcrumb.tenantDetails"),
              show: true,
              onClick: () =>
                navigate(
                  ADMIN_PROTECTED_ROUTES.TENANT_DETAILS.replace(
                    ":id",
                    tenantId!
                  )
                ),
            },
            {
              label: t("admin.productAssignment.breadcrumb.assignProduct"),
              show: true,
            },
          ]}
        />

        {/* Action buttons */}
        <div className="flex justify-between items-center mb-1">
          <div>
            <h1 className="page-title  mt-[16px]">
              {t("admin.productAssignment.pageTitle")}
            </h1>
            <p className="page-subtitle capitalize">
              {tenantResponse?.data?.[0]?.name}
            </p>
          </div>
          <div className="flex space-x-3">
            <Button
              variant={currentStep === 1 ? "ghost" : "outline"}
              onClick={() => navigate(-1)}
            >
              {currentStep === 1
                ? t("admin.productAssignment.actions.cancel")
                : "Back"}
            </Button>
            {currentStep === 1 && (
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    await onSaveAsDraft();
                  } catch (error) {
                    console.error("Error saving as draft:", error);
                  }
                }}
                disabled={isAssigning}
              >
                {isAssigning
                  ? "Saving..."
                  : t("admin.productAssignment.actions.saveAsDraft")}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col border-t border-gray-200 h-full">
        <div className="flex flex-1 min-h-0">
          {/* Left Column: Step Navigation */}
          <div className="xl:w-64 px-2 py-6 flex-shrink-0">
            <nav>
              {steps.map((step, index) => (
                <div
                  key={step.id}
                  className={`flex items-start py-3 gap-2 text-[#414651] ${
                    currentStep === step.id
                      ? "font-medium"
                      : "font-normal opacity-80"
                  }`}
                >
                  <div className="w-[40px] h-[40px]">
                    <img
                      src={step.icon}
                      alt={step.title}
                      className={`${
                        index === 1 ? "w-[60px] h-[60px]" : "w-[40px] h-[40px]"
                      } ${
                        currentStep === step.id ? "opacity-100" : "opacity-50"
                      } object-contain`}
                    />
                  </div>
                  <div className="hidden xl:block">
                    <div className="text-sm font-medium">{t(step.title)}</div>
                    <div className="text-xs page-subtitle">
                      {t(step.subtitle)}
                    </div>
                  </div>
                </div>
              ))}
            </nav>
          </div>

          {/* Right Column: Current Step Content */}
          <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
            <div className="flex-1 overflow-y-auto">
              <h2 className="text-xl font-semibold pt-6 pl-6">
                {t(steps[currentStep - 1]?.pageTitle || "")}
              </h2>
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductAssignmentLayout;
